import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import { Search, Trash2, Eye, X, RefreshCw } from 'lucide-react';
import { colors, fontFamily, optionStyle, selectSurface } from '../../theme';

const card = { background: colors.cardBg, boxShadow: colors.shadowCard, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.25rem', marginBottom: '1.25rem' };
const inputStyle = { padding: '0.5rem 0.75rem', background: colors.cardBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, color: colors.textPrimary, fontFamily, fontSize: '0.85rem' };
const btnPrimary = { padding: '0.5rem 1rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 4, cursor: 'pointer', fontFamily, display: 'flex', alignItems: 'center', gap: '0.4rem' };
const btnCancel = { padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.1)', border: 'none', color: colors.textPrimary, borderRadius: 4, cursor: 'pointer', fontFamily };
const btnDanger = { padding: '0.5rem 1rem', background: 'rgba(220,53,69,0.2)', border: `1px solid ${colors.negative}`, color: colors.negative, borderRadius: 4, cursor: 'pointer', fontFamily };

const statusColors = {
  pending: { bg: 'rgba(255,193,7,0.15)', color: colors.pending },
  won:     { bg: 'rgba(40,167,69,0.15)',  color: colors.odds },
  lost:    { bg: 'rgba(220,53,69,0.15)',  color: colors.negative },
  void:    { bg: 'rgba(108,117,125,0.15)', color: '#6c757d' },
  cancelled: { bg: 'rgba(108,117,125,0.15)', color: '#6c757d' },
};

export default function TicketManagement() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const perPage = 20;

  useEffect(() => { loadTickets(); }, [page, statusFilter]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await api.admin.listTickets(page, perPage, statusFilter || null, search);
      setTickets(data.tickets || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); loadTickets(); };

  const viewTicket = async (ticketNumber) => {
    try {
      const data = await api.admin.getTicket(ticketNumber);
      setSelectedTicket(data.ticket);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load ticket');
    }
  };

  const deleteTicket = async (ticketNumber) => {
    try {
      setActionLoading(true);
      await api.admin.deleteTicket(ticketNumber);
      setShowDeleteConfirm(null);
      loadTickets();
      if (selectedTicket?.ticket_number === ticketNumber) setSelectedTicket(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (s) => s ? new Date(s).toLocaleString() : '—';

  const StatusBadge = ({ status }) => {
    const c = statusColors[status] || statusColors.void;
    return <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20, background: c.bg, color: c.color }}>{status?.toUpperCase()}</span>;
  };

  const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalBox = { background: colors.bgGradient, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.5rem', width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' };
  const detailLabel = { color: colors.textMuted, fontSize: '0.78rem', display: 'block', marginBottom: '0.2rem' };
  const detailVal = { color: colors.textPrimary, fontWeight: 600, fontSize: '0.88rem', margin: 0 };

  return (
    <div style={{ fontFamily, color: colors.textPrimary }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h2 style={{ color: colors.textPrimary, margin: 0, fontSize: '1.3rem' }}>Ticket Management</h2>
        <button onClick={loadTickets} style={btnPrimary}><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Search */}
      <div style={card}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.82rem', marginBottom: '0.3rem' }}>Search</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by ticket number or description..."
                style={{ ...inputStyle, width: '100%', paddingLeft: '2rem', boxSizing: 'border-box' }}
              />
              <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: colors.textMuted }} />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.82rem', marginBottom: '0.3rem' }}>Status</label>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{ ...inputStyle, background: selectSurface }}>
              <option style={optionStyle} value="">All</option>
              <option style={optionStyle} value="pending">Pending</option>
              <option style={optionStyle} value="won">Won</option>
              <option style={optionStyle} value="lost">Lost</option>
              <option style={optionStyle} value="void">Void</option>
            </select>
          </div>
          <button type="submit" style={btnPrimary}>Search</button>
        </form>
      </div>

      {/* Table */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.goldMuted}`, background: colors.sectionBg }}>
                {['Ticket #', 'Type', 'Customer', 'Amount', 'Status', 'Created', 'Actions'].map(h => (
                  <th key={h} style={{ color: colors.textMuted, padding: '0.65rem 0.75rem', textAlign: 'left', fontWeight: 'normal' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>Loading...</td></tr>
              ) : tickets.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>No tickets found</td></tr>
              ) : tickets.map(ticket => (
                <tr key={ticket.ticket_number || ticket.id} style={{ borderBottom: `1px solid ${colors.sectionBg}` }}>
                  <td style={{ padding: '0.6rem 0.75rem', color: colors.gold, fontFamily: 'monospace', fontWeight: 600 }}>{ticket.ticket_number || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>
                    <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20, background: ticket.bet_type_category === 'parlay' ? 'rgba(100,50,150,0.3)' : 'rgba(50,80,150,0.3)', color: ticket.bet_type_category === 'parlay' ? colors.freePlay : '#60a5fa' }}>
                      {ticket.bet_type_category === 'parlay' ? `Parlay (${ticket.num_legs}L)` : 'Single'}
                    </span>
                  </td>
                  <td style={{ padding: '0.6rem 0.75rem', color: colors.textPrimary }}>{ticket.user_name || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: colors.gold, fontWeight: 600 }}>
                    ${ticket.amount?.toFixed(2)}{ticket.is_free_play && <span style={{ color: colors.freePlay, fontSize: '0.7rem', marginLeft: 4 }}>FP</span>}
                  </td>
                  <td style={{ padding: '0.6rem 0.75rem' }}><StatusBadge status={ticket.status} /></td>
                  <td style={{ padding: '0.6rem 0.75rem', color: colors.textMuted, fontSize: '0.78rem' }}>{formatDate(ticket.created_at)}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => viewTicket(ticket.ticket_number)} title="View" style={{ background: 'none', border: 'none', color: colors.gold, cursor: 'pointer', padding: 2 }}><Eye size={15} /></button>
                      {ticket.status === 'pending' && (
                        <button onClick={() => setShowDeleteConfirm(ticket.ticket_number)} title="Delete" style={{ background: 'none', border: 'none', color: colors.negative, cursor: 'pointer', padding: 2 }}><Trash2 size={15} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {total > perPage && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', borderTop: `1px solid ${colors.goldMuted}` }}>
            <span style={{ color: colors.textMuted, fontSize: '0.82rem' }}>
              {(page-1)*perPage+1}–{Math.min(page*perPage, total)} of {total}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} style={{ ...btnCancel, opacity: page === 1 ? 0.4 : 1 }}>Previous</button>
              <button onClick={() => setPage(p => p+1)} disabled={page*perPage >= total} style={{ ...btnCancel, opacity: page*perPage >= total ? 0.4 : 1 }}>Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div style={modalOverlay}>
          <div className="scrollbar-hide" style={modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: `1px solid ${colors.goldMuted}` }}>
              <h3 style={{ color: colors.textPrimary, margin: 0, fontSize: '1.05rem' }}>Ticket: {selectedTicket.ticket_number}</h3>
              <button onClick={() => setSelectedTicket(null)} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', marginBottom: '1rem' }}>
              {[
                ['Type', selectedTicket.bet_type_category === 'parlay' ? `Parlay (${selectedTicket.num_legs} legs)` : 'Single Bet'],
                ['Status', null],
                ['Amount', `$${selectedTicket.amount?.toFixed(2)}`],
                ['Odds', selectedTicket.odds || selectedTicket.combined_odds || '—'],
                ['Customer', selectedTicket.user_details?.name || selectedTicket.user_name],
                ['Free Play', selectedTicket.is_free_play ? 'Yes' : 'No'],
                ['Created', formatDate(selectedTicket.created_at)],
                ['Settled', formatDate(selectedTicket.settled_at)],
              ].map(([label, val]) => (
                <div key={label}>
                  <span style={detailLabel}>{label}</span>
                  {label === 'Status' ? <StatusBadge status={selectedTicket.status} /> : <p style={detailVal}>{val}</p>}
                </div>
              ))}
            </div>
            {selectedTicket.description && (
              <div style={{ marginBottom: '0.75rem' }}>
                <span style={detailLabel}>Description</span>
                <p style={detailVal}>{selectedTicket.description}</p>
              </div>
            )}
            {selectedTicket.payout && (
              <div style={{ marginBottom: '0.75rem' }}>
                <span style={detailLabel}>Payout</span>
                <p style={{ ...detailVal, color: colors.odds }}>${selectedTicket.payout?.toFixed(2)}</p>
              </div>
            )}
            {selectedTicket.legs?.length > 0 && (
              <div style={{ marginBottom: '0.75rem' }}>
                <span style={{ ...detailLabel, marginBottom: '0.5rem', display: 'block' }}>Parlay Legs</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {selectedTicket.legs.map((leg, i) => (
                    <div key={i} style={{ background: colors.sectionBg, borderRadius: 4, padding: '0.5rem 0.75rem' }}>
                      <p style={{ color: colors.textPrimary, margin: '0 0 0.2rem 0', fontSize: '0.85rem', fontWeight: 600 }}>{leg.description || leg.selection}</p>
                      <p style={{ color: colors.textMuted, margin: 0, fontSize: '0.78rem' }}>{leg.away_team} @ {leg.home_team} | {leg.odds}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', paddingTop: '0.75rem', borderTop: `1px solid ${colors.goldMuted}`, justifyContent: 'flex-end' }}>
              {selectedTicket.status === 'pending' && (
                <button onClick={() => setShowDeleteConfirm(selectedTicket.ticket_number)} style={btnDanger}>Delete Ticket</button>
              )}
              <button onClick={() => setSelectedTicket(null)} style={btnCancel}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, maxWidth: 380 }}>
            <h3 style={{ color: colors.textPrimary, margin: '0 0 0.75rem 0' }}>Confirm Delete</h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.25rem', fontSize: '0.88rem' }}>
              Are you sure you want to delete ticket <strong style={{ color: colors.textPrimary }}>{showDeleteConfirm}</strong>? This will refund the bet amount to the customer.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDeleteConfirm(null)} disabled={actionLoading} style={btnCancel}>Cancel</button>
              <button onClick={() => deleteTicket(showDeleteConfirm)} disabled={actionLoading} style={{ ...btnDanger, opacity: actionLoading ? 0.5 : 1 }}>
                {actionLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
